import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Briefcase, Clock, Play, Square, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { companion } from "@/context/CompanionAuthContext";
import { useCompanionAuth } from "@/context/CompanionAuthContext";
import { STATUS_LABELS, STATUS_COLORS, todayISO, formatDateTime, hoursBetween } from "./utils";

const CompanionToday = () => {
  const { user } = useCompanionAuth();
  const [jobs, setJobs] = useState([]);
  const [notices, setNotices] = useState([]);
  const [todos, setTodos] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const today = todayISO();
    try {
      const [j, n, t, c] = await Promise.all([
        companion.get(`/jobs?mine=true&date_from=${today}&date_to=${today}`).then((r) => r.data),
        companion.get("/notices").then((r) => r.data),
        companion.get("/todos").then((r) => r.data),
        companion.get("/time/current").then((r) => r.data),
      ]);
      setJobs(j); setNotices(n); setTodos(t); setCurrent(c);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pinnedNotice = useMemo(() => notices.find((n) => n.is_pinned) || notices[0], [notices]);
  const pendingTodos = useMemo(() => todos.filter((t) => !t.done).slice(0, 4), [todos]);

  const startClock = async (jobId) => {
    setBusy(true);
    try {
      const { data } = await companion.post("/time/start", { job_id: jobId });
      setCurrent(data);
      toast.success("Clocked in.");
    } catch { toast.error("Couldn't clock in."); }
    finally { setBusy(false); }
  };

  const stopClock = async () => {
    setBusy(true);
    try {
      await companion.post("/time/stop", {});
      setCurrent(null);
      toast.success("Clocked out.");
    } catch { toast.error("Couldn't clock out."); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <div className="grid place-items-center py-24"><Loader2 className="h-7 w-7 animate-spin text-crest-green" /></div>;
  }

  return (
    <div className="px-4 py-5 space-y-5" data-testid="companion-today">
      <header>
        <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Today</p>
        <h1 className="mt-1 font-display text-2xl font-bold text-crest-navy">
          Morning, {user?.name?.split(" ")[0] || "team"} 👋
        </h1>
      </header>

      {/* Time clock card */}
      <section className="rounded-lg border border-border bg-white p-5" data-testid="time-clock-card">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 grid place-items-center rounded-md bg-crest-green-100 text-crest-green">
            <Clock className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            {current ? (
              <>
                <div className="text-xs uppercase tracking-[0.18em] text-crest-gold">Clocked in</div>
                <div className="font-display font-semibold text-crest-navy truncate">
                  Since {formatDateTime(current.started_at)} · {hoursBetween(current.started_at, new Date().toISOString()).toFixed(1)} h
                </div>
              </>
            ) : (
              <>
                <div className="text-xs uppercase tracking-[0.18em] text-crest-navy/60">Off the clock</div>
                <div className="font-display font-semibold text-crest-navy">Start your day</div>
              </>
            )}
          </div>
          {current ? (
            <button
              onClick={stopClock}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-crest-navy text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
              data-testid="clock-out-btn"
            >
              <Square className="h-4 w-4" /> Clock out
            </button>
          ) : (
            <button
              onClick={() => startClock(jobs[0]?.id)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-md bg-crest-green text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
              data-testid="clock-in-btn"
            >
              <Play className="h-4 w-4" /> Clock in
            </button>
          )}
        </div>
      </section>

      {/* Notice */}
      {pinnedNotice && (
        <Link to="/companion/notices" className="block rounded-lg bg-crest-gold/15 border border-crest-gold/30 p-4" data-testid="today-notice">
          <div className="text-[11px] uppercase tracking-[0.18em] text-crest-gold font-medium">
            {pinnedNotice.is_pinned ? "Pinned notice" : "Latest notice"}
          </div>
          <div className="mt-1 font-display font-semibold text-crest-navy">{pinnedNotice.title}</div>
          <div className="mt-1 text-sm text-crest-navy/80 line-clamp-2">{pinnedNotice.body}</div>
        </Link>
      )}

      {/* Today's jobs */}
      <section>
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-crest-navy">Jobs today</h2>
          <Link to="/companion/jobs" className="text-xs uppercase tracking-[0.18em] text-crest-green font-medium">All jobs →</Link>
        </div>
        {jobs.length === 0 ? (
          <div className="mt-3 rounded-lg border border-dashed border-border bg-white p-6 text-center text-sm text-muted-foreground">
            Nothing scheduled for today. Enjoy the breather.
          </div>
        ) : (
          <ul className="mt-3 space-y-2.5">
            {jobs.map((j) => (
              <li key={j.id}>
                <Link to={`/companion/jobs/${j.id}`} className="flex items-start gap-3 rounded-lg border border-border bg-white p-4 hover:border-crest-green transition-colors"
                      data-testid={`today-job-${j.id}`}>
                  <div className="h-10 w-10 grid place-items-center rounded-md bg-crest-green-100 text-crest-green shrink-0">
                    <Briefcase className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-crest-navy truncate">{j.customer_name}</div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5 truncate">
                      <MapPin className="h-3.5 w-3.5 shrink-0" /> {j.address}{j.postcode ? `, ${j.postcode}` : ""}
                    </div>
                    <div className="mt-2 inline-flex items-center gap-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded ${STATUS_COLORS[j.status]}`}>{STATUS_LABELS[j.status]}</span>
                      {j.scheduled_time_start && <span className="text-[11px] text-muted-foreground">{j.scheduled_time_start}</span>}
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-crest-navy/40 mt-1" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Open todos */}
      {pendingTodos.length > 0 && (
        <section>
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-crest-navy">Your to-dos</h2>
            <Link to="/companion/todos" className="text-xs uppercase tracking-[0.18em] text-crest-green font-medium">All →</Link>
          </div>
          <ul className="mt-3 space-y-2">
            {pendingTodos.map((t) => (
              <li key={t.id} className="flex items-start gap-3 rounded-lg border border-border bg-white p-3">
                <ClipboardList className="h-4 w-4 text-crest-green mt-0.5 shrink-0" />
                <span className="text-sm text-crest-navy">{t.title}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default CompanionToday;
